// The only component that knows what "editable" looks like on the Opportunities list (spec 008 D3):
// resting content, pointer cursor, hover tint, a tooltip naming the action, the PROJECTS_UPDATE gate,
// and the design-system PopoverHandler that anchors the editor to the cell. Change the affordance here
// and all five editable cells follow.
//
// D2: the affordance is hover-only. No persistent marker, no dashed underline — with five editable
// cells per row a resting marker would draw a border under most of the table.
// D7: positioning is PopoverHandler's job. It is already the anchored-popover primitive in this exact
// table (MissingDatePopover), so there is no flip/fit logic to hand-roll here.
import React from 'react';
import {PopoverHandler, Tooltip} from '@forecast-it/design-system';
import Styled from 'styled-components';
import {canEditRow} from '../opportunityInlineEdit';

export interface EditableCellProps {
	// Resting cell content — exactly what the column renders when the cell is not editable.
	children: React.ReactNode;
	// The popover body. `close` dismisses the popover (editors call it after a commit or a cancel).
	editor: (args: {close: () => void}) => React.ReactNode;
	// Tooltip naming the action, e.g. "Edit salesperson".
	tooltip: string;
	dataCy: string;
	// Per-cell opt-out for a row whose field has no writable source (§2.5). Defaults to true; the
	// PROJECTS_UPDATE gate applies on top of it either way.
	editable?: boolean;
	// Why this particular cell cannot be edited, shown on hover when `editable` is false. A cell that
	// silently does nothing when clicked reads as broken; saying why does not. Deliberately not used for
	// the permission case - see D4 below.
	readOnlyTooltip?: string;
}

// The hover tint and pointer live on the trigger, not the StyledCell: the DS cell also hosts
// non-editable columns, and the tint must cover only the clickable region.
// No pointer and no hover tint: this cell is not clickable, and dressing it like the editable ones
// would promise an editor that never opens. It exists only to carry the title.
const ReadOnly = Styled.span`
	display: inline-flex;
	align-items: center;
	min-width: 0;
	max-width: 100%;
`;

const Trigger = Styled.button.attrs({type: 'button'})`
	display: inline-flex;
	align-items: center;
	min-width: 0;
	max-width: 100%;
	padding: 2px 4px;
	border: none;
	background: none;
	font: inherit;
	color: inherit;
	text-align: inherit;
	margin: -2px -4px;
	border-radius: 4px;
	cursor: pointer;
	&:hover {
		background: #eef0ff;
	}
`;

// The editor body needs the popover's open state to close itself, so it runs in its own component
// inside PopoverHandler's context (where usePopoverState is available) — the same shape
// MissingDatePopover uses for its card.
const EditorHost = ({editor}: Pick<EditableCellProps, 'editor'>): JSX.Element => {
	const {setOpen} = PopoverHandler.usePopoverState();
	return <>{editor({close: () => setOpen(false)})}</>;
};

export const EditableCell = ({
	children,
	editor,
	tooltip,
	dataCy,
	editable = true,
	readOnlyTooltip,
}: EditableCellProps): JSX.Element => {
	// D4: without PROJECTS_UPDATE the cell renders precisely as it does today. Not a disabled editor -
	// no pointer, no tint, no click target, and no explanation either: someone who may not edit anything
	// does not need a per-cell reason, that would just be noise on every row.
	if (!canEditRow()) {
		return <>{children}</>;
	}
	// Editable elsewhere in this row, but not this cell. Say why on hover rather than leaving a cell that
	// looks identical to its editable neighbours and does nothing when clicked.
	//
	// The design-system Tooltip is usable HERE and not on the trigger below: it destructures only the
	// props it knows and drops the rest, and calls preventDefault() on the click unless `interactable` is
	// set - which kills the click that opens an editor. This cell has no click to kill, so it can have the
	// styled tooltip instead of the browser's plain one. It is hover-only either way: the design system
	// merges its trigger onto this span without a tabindex, and adding one would put every read-only cell
	// in the tab order - on a table where most rows are read-only, that is a lot of stops for something
	// you cannot act on.
	if (!editable) {
		return readOnlyTooltip ? (
			<Tooltip content={readOnlyTooltip}>
				<ReadOnly>{children}</ReadOnly>
			</Tooltip>
		) : (
			<>{children}</>
		);
	}
	return (
		<PopoverHandler>
			<PopoverHandler.Trigger>
				{/* This MUST be a plain DOM element, and Tooltip MUST NOT sit between it and Trigger.
				    PopoverHandler.Trigger is a Radix trigger with asChild, so it merges its onClick and ref
				    onto its immediate child via Slot. The design-system Tooltip destructures only
				    {content, interactable, children, width, onOpenChange} and drops every other prop, so
				    those never reach the DOM - and when `interactable` is falsy it also calls
				    preventDefault() on the click. Nesting them the other way round left the cell showing its
				    tooltip while clicking did nothing at all. `title` names the action instead, the same way
				    MissingDatePopover's RedLabel does. */}
				<Trigger data-cy={dataCy} title={tooltip}>
					{children}
				</Trigger>
			</PopoverHandler.Trigger>
			{/* The design-system Content defaults to align='end', which pins the popover's right edge to the
			    trigger so it opens off to the left. Centre it on the cell instead, so the arrow points at the
			    value being edited - Radix still flips the alignment on its own if it would collide with the
			    viewport edge, which matters for the first and last columns. */}
			<PopoverHandler.Content align="center" showArrow>
				<EditorHost editor={editor} />
			</PopoverHandler.Content>
		</PopoverHandler>
	);
};

export default EditableCell;
